import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Space, Spin, Alert, Tag, Typography } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

/**
 * PPTXViewer - Secure PowerPoint viewer
 *
 * Extracts and displays PPTX slides server-side to ensure confidential files
 * never leave your infrastructure. Shows slide content as structured HTML.
 */
const PPTXViewer = ({
  documentId,
  filename,
  onDownload,
  containerStyle = {},
  showToolbar = true,
  toolbarPosition = 'top'
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presentationInfo, setPresentationInfo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [scale, setScale] = useState(1.0);

  const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Get auth header
  const getAuthHeader = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
  };

  // Reset state when documentId changes
  useEffect(() => {
    setSlideIndex(0);
    setCurrentSlide(null);
    setPresentationInfo(null);
    setError(null);
    setScale(1.0);
  }, [documentId]);

  // Load presentation info on mount or when documentId changes
  useEffect(() => {
    const loadPresentationInfo = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${API_URL}/archive/pptx-preview/${documentId}`,
          { headers: getAuthHeader() }
        );

        setPresentationInfo(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error loading presentation info:', err);
        setError(err.response?.data?.detail || err.message);
        setLoading(false);
      }
    };

    if (documentId) {
      loadPresentationInfo();
    }
  }, [documentId, API_URL]);

  // Load slide image when index changes
  useEffect(() => {
    if (!presentationInfo) return;

    const loadSlide = async () => {
      setCurrentSlide(null); // Clear current slide while loading

      try {
        const response = await axios.get(
          `${API_URL}/archive/pptx-slide/${documentId}/${slideIndex}`,
          { headers: getAuthHeader() }
        );

        setCurrentSlide({
          index: slideIndex,
          image: response.data.image
        });
      } catch (err) {
        console.error('Error loading slide:', err);
        setError(err.response?.data?.detail || err.message);
      }
    };

    loadSlide();
  }, [slideIndex, presentationInfo, documentId, API_URL]);

  const goToPrevSlide = () => {
    setSlideIndex((prev) => Math.max(prev - 1, 0));
  };

  const goToNextSlide = () => {
    if (presentationInfo) {
      setSlideIndex((prev) => Math.min(prev + 1, presentationInfo.slide_count - 1));
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const handleDownloadClick = () => {
    if (onDownload) {
      onDownload();
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" tip="Loading presentation..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Failed to Load Presentation"
        description={error}
        type="error"
        showIcon
        action={
          onDownload && (
            <Button onClick={handleDownloadClick} icon={<DownloadOutlined />}>
              Download
            </Button>
          )
        }
        style={{ margin: 20 }}
      />
    );
  }

  // Toolbar component
  const toolbar = (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: toolbarPosition === 'top' ? '1px solid #e8e8e8' : 'none',
        borderTop: toolbarPosition === 'bottom' ? '1px solid #e8e8e8' : 'none',
        backgroundColor: 'var(--rlc-surface-subtle)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Space size="large">
        {/* Slide Navigation */}
        <Space>
          <Button
            icon={<LeftOutlined />}
            onClick={goToPrevSlide}
            disabled={slideIndex === 0}
            title="Previous slide"
          />
          <span style={{ minWidth: '120px', textAlign: 'center' }}>
            Slide {slideIndex + 1} / {presentationInfo?.slide_count || '...'}
          </span>
          <Button
            icon={<RightOutlined />}
            onClick={goToNextSlide}
            disabled={slideIndex >= (presentationInfo?.slide_count - 1)}
            title="Next slide"
          />
        </Space>

        {/* Zoom Controls */}
        <Space>
          <Button
            icon={<ZoomOutOutlined />}
            onClick={zoomOut}
            disabled={scale <= 0.5}
            title="Zoom out"
          />
          <span style={{ minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <Button
            icon={<ZoomInOutlined />}
            onClick={zoomIn}
            disabled={scale >= 3.0}
            title="Zoom in"
          />
          <Button
            onClick={resetZoom}
            disabled={scale === 1.0}
            title="Reset zoom"
            size="small"
          >
            Reset
          </Button>
        </Space>
      </Space>
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', ...containerStyle }}>
      {/* Top Toolbar */}
      {showToolbar && toolbarPosition === 'top' && toolbar}

      {/* Slide Content - Display as Image */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#525659',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {currentSlide && currentSlide.image ? (
          <img
            src={currentSlide.image}
            alt={`Slide ${slideIndex + 1}`}
            style={{
              maxWidth: `${scale * 100}%`,
              maxHeight: `${scale * 100}%`,
              height: 'auto',
              width: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              borderRadius: '2px',
              backgroundColor: 'white',
              transform: `scale(${scale})`,
              transformOrigin: 'center center'
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Spin size="large" tip="Loading slide..." />
          </div>
        )}
      </div>

      {/* Bottom Toolbar */}
      {showToolbar && toolbarPosition === 'bottom' && toolbar}
    </div>
  );
};

PPTXViewer.propTypes = {
  documentId: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  onDownload: PropTypes.func,
  containerStyle: PropTypes.object,
  showToolbar: PropTypes.bool,
  toolbarPosition: PropTypes.oneOf(['top', 'bottom'])
};

export default PPTXViewer;
